import data from "../../shared/sharedData";
import {
  normalizeCountyLabel,
  getCountySourceFromPwd,
  getStateSelectionOptions,
} from "../../../../global/utils/countyHelper";

const EMPTY_MSA_DATA = {};

const restoreLinkedWorksite = (_e, formApi) => {
  const values = formApi.getState().values;
  if (values.faWorksiteLinked && values._savedPrimaryWorksite) {
    try {
      const saved = JSON.parse(values._savedPrimaryWorksite);
      Object.entries(saved).forEach(([field, val]) =>
        formApi.change(field, val)
      );
    } catch (_err) {}
  }
};

const naCountyOption = {
  bls: "N/A",
  blsAreaCode: 0,
  blsAreaId: 0,
  label: "N/A",
  value: 0,
};

const conditionalClearSchema = [
  { clear: "primaryWorksiteAddr1", when: "primaryWorksiteAddr1", is: "N/A" },
  { clear: "primaryWorksiteAddr2", when: "primaryWorksiteAddr2", is: "N/A" },
  {
    clear: "primaryWorksiteBlsArea",
    when: "primaryWorksiteBlsArea",
    is: "N/A",
  },
  {
    clear: "primaryWorksiteBlsAreaCode",
    when: "primaryWorksiteBlsAreaCode",
    is: 0,
  },
  { clear: "primaryWorksiteCity", when: "primaryWorksiteCity", is: "N/A" },
  { clear: "primaryWorksiteCounty", when: "primaryWorksiteState", is: "N/A" },
  { clear: "primaryWorksiteState", when: "primaryWorksiteState", is: "N/A" },
  {
    clear: "primaryWorksitePostalCode",
    when: "primaryWorksitePostalCode",
    is: "N/A",
  },
];

const setTheseFieldsToNA = [
  { set: "primaryWorksiteAddr1", to: "N/A" },
  { set: "primaryWorksiteAddr2", to: "N/A" },
  { set: "primaryWorksiteBlsArea", to: "N/A" },
  { set: "primaryWorksiteBlsAreaCode", to: 0 },
  { set: "primaryWorksiteCity", to: "N/A" },
  { set: "primaryWorksiteCounty", to: naCountyOption },
  { set: "primaryWorksiteState", to: "N/A" },
  { set: "primaryWorksitePostalCode", to: "N/A" },
];

const WorksiteMappings = [
  { to: "primaryWorksiteAddr1", from: "worksiteAddress1" },
  { to: "primaryWorksiteAddr2", from: "worksiteAddress2" },
  { to: "primaryWorksiteBlsArea", from: "worksiteBlsArea" },
  { to: "primaryWorksiteCity", from: "worksiteCity" },
  { to: "primaryWorksiteCounty", from: "worksiteCountyTownship" },
  { to: "primaryWorksiteState", from: "worksiteState" },
  { to: "primaryWorksitePostalCode", from: "worksitePostalCode" },
];

export default {
  name: `Worksite Information`,
  prefix: "F.b/c",
  pageInfo: {
    text: `<b><u>Note:</u></b> It is important for the employer to define the area of intended employment with as much
     geographic specificity as possible by identifying <u>every</u> worksite location where work will be performed. 
     This information is used for purposes of reviewing and verifying regulatory compliance with advertising, 
     notice posting, and prevailing wage requirements.`,
    coloring: "grey",
  },
  sections: [
    {
      // isVisible: {
      //   when: "pwdHasMultipleWorksites",
      //   is: [false, undefined, null, ""],
      //   operator: "!==",
      // },
      isVisible: {
        when: (formApi) => {
          const multipleWorksites =
            formApi.getState().values.pwdHasMultipleWorksites;
          const primaryWorksiteType =
            formApi.getState().values.primaryWorksiteType;
          return (
            !!multipleWorksites &&
            (primaryWorksiteType !== "4" || !primaryWorksiteType)
          );
        },
        is: "addWorksiteFaCheck",
        operator: "customValidation",
      },
      fields: [
        {
          formId: "9089",
          buttonText: "Select Worksite",
          buttonLabel: "Select a worksite location to populate this section",
          widget: "worksite_linking",
          onSubmit: (
            counties,
            worksite,
            setFieldValue,
            toggleModal,
            formApi
          ) => {
            if (!!worksite) {
              const selectedWorksiteType =
                worksite.worksiteType ??
                worksite.primaryWorksiteType ??
                worksite.type ??
                "1";
              const selectedAddress1 =
                worksite.worksiteAddress1 ??
                worksite.primaryWorksiteAddr1 ??
                worksite.address1 ??
                worksite.address ??
                null;
              const selectedAddress2 =
                worksite.worksiteAddress2 ??
                worksite.primaryWorksiteAddr2 ??
                worksite.address2 ??
                null;
              const selectedPostalCode =
                worksite.worksitePostalCode ??
                worksite.primaryWorksitePostalCode ??
                worksite.postalCode ??
                null;

              let countyTownship = worksite.worksiteCountyTownship;
              // The county data can come in as a string of JSON; parse it if so.
              if (countyTownship && typeof countyTownship === "string") {
                try {
                  // Attempt to parse it as JSON
                  countyTownship = JSON.parse(countyTownship);
                } catch (e) {
                  // If parsing fails, it's not JSON. Assume it's a plain string label
                  // and create an object that matches the expected structure.
                  countyTownship = {
                    label: countyTownship,
                    value: countyTownship,
                  };
                }
              }

              let bls = null;
              let blsCode = null;
              let finalCounty = null;
              let useFallback = true;
              const formStateValues = formApi.getState().values;

              // 1. Prioritize the PWD's own county data if it exists on the form state.
              if (formStateValues._blsCounties) {
                try {
                  const parsedBlsCounties = JSON.parse(
                    formStateValues._blsCounties
                  );
                  const msaKey = worksite.worksiteBlsArea
                    ? Object.keys(parsedBlsCounties).find(
                        (k) =>
                          k.toUpperCase() ===
                          worksite.worksiteBlsArea.toUpperCase()
                      )
                    : undefined;

                  if (msaKey) {
                    const msaData = parsedBlsCounties[msaKey];
                    const stateKey = worksite.worksiteState
                      ? Object.keys(msaData).find(
                          (k) =>
                            k.toUpperCase() ===
                            worksite.worksiteState.toUpperCase()
                        )
                      : undefined;

                    if (stateKey) {
                      const countiesForState = msaData[stateKey];
                      const normalizedSearch = normalizeCountyLabel(
                        countyTownship?.label
                      );

                      const foundCounty = countiesForState.find(
                        (c) =>
                          normalizeCountyLabel(c.label) === normalizedSearch
                      );

                      if (foundCounty) {
                        finalCounty = foundCounty;
                        bls = foundCounty.bls;
                        blsCode = foundCounty.blsAreaCode;
                        useFallback = false; // Success! No need for fallback.
                      }
                    }
                  }
                } catch (e) {
                  console.error(
                    "Failed to parse _blsCounties JSON, using fallback logic.",
                    e
                  );
                }
              }

              // 2. Fallback to searching the main `counties` object if needed.
              if (useFallback) {
                const stateCounties = counties[worksite.worksiteState] || [];
                if (worksite.worksiteBlsArea) {
                  const incomingMsaUpper =
                    worksite.worksiteBlsArea.toUpperCase();
                  const countiesInMsa = stateCounties.filter(
                    (c) => c.bls.toUpperCase() === incomingMsaUpper
                  );

                  if (countiesInMsa.length > 0) {
                    bls = countiesInMsa[0].bls;
                    blsCode = countiesInMsa[0].blsAreaCode;

                    if (worksite.worksiteCountyTownship) {
                      const normalizedSearch = normalizeCountyLabel(
                        countyTownship?.label
                      );
                      const foundCountyInMsa = countiesInMsa.find(
                        (c) =>
                          normalizeCountyLabel(c.label) === normalizedSearch
                      );
                      if (foundCountyInMsa) {
                        finalCounty = foundCountyInMsa;
                      }
                    }
                  }
                }
              }

              // 3. Map all other non-geographic fields.
              WorksiteMappings.forEach((mapping) => {
                const { to, from } = mapping;
                if (
                  to === "primaryWorksiteCounty" ||
                  to === "primaryWorksiteBlsArea"
                ) {
                  return;
                }
                const value =
                  from === "worksiteAddress1"
                    ? selectedAddress1
                    : from === "worksiteAddress2"
                    ? selectedAddress2
                    : from === "worksitePostalCode"
                    ? selectedPostalCode
                    : worksite[from];
                if (value !== undefined && value !== null) {
                  setFieldValue(to, value);
                }
              });

              setFieldValue("primaryWorksiteType", selectedWorksiteType);

              // 4. Set the validated geographic fields.
              setFieldValue("primaryWorksiteCounty", finalCounty);
              setFieldValue("primaryWorksiteBlsArea", bls);
              setFieldValue("primaryWorksiteBlsAreaCode", blsCode);
              setFieldValue("faWorksiteLinked", true);
              // 5. Snapshot the resolved worksite so it can be restored if the
              //    user temporarily switches to option 4 and then back.
              setFieldValue(
                "_savedPrimaryWorksite",
                JSON.stringify({
                  primaryWorksiteAddr1: selectedAddress1 ?? null,
                  primaryWorksiteAddr2: selectedAddress2 ?? null,
                  primaryWorksiteCity: worksite.worksiteCity ?? null,
                  primaryWorksiteState: worksite.worksiteState ?? null,
                  primaryWorksitePostalCode: selectedPostalCode ?? null,
                  primaryWorksiteCounty: finalCounty,
                  primaryWorksiteBlsArea: bls,
                  primaryWorksiteBlsAreaCode: blsCode,
                })
              );
            }
            toggleModal();
          },
        },
      ],
    },
    {
      fields: [
        {
          isGroup: {
            code: "F.a.1",
            required: true,
            machine_name: "primaryWorksiteType",
            label:
              "Type of worksite location that best describes where work will be performed (Choose Only One) ",
            tooltip: `Identify whether the worksite location that best describes where work will be performed is business premises, employer's private household (including live-in and domestic household workers), employee's private residence (when work is performed directly out of the employee's residence), or no <u>one</u> 
              specific worksite address or physical location (if there is more than one worksite location or geographic area). <u>Only mark one box</u>. 
              <br/><br/>
              <b><u>Note:</u></b> If submitting this form non-electronically and "No <u>one</u> specific worksite address or physical location" 
              is marked in question 1, enter "N/A" or "0" (zero), as appropriate, in questions 2-7, mark "N/A" in question 8 and 8a, and continue to Section F.b.  `,
            coloring: "none",
            clearBtn: {
              clearSchema: ["isMultipleLocations", "isAppdendixBAttached"],
              conditionalClearSchema: conditionalClearSchema,
              hide: {
                when: "occupationType",
                is: "professional_athlete",
              },
            },
            fields: [
              {
                widget: "radio",
                choice: "Business Premises",
                value: "1",
                validate: ["required"],
                conditionalClearSchema: conditionalClearSchema,
                clearSchema: ["isMultipleLocations", "isAppdendixBAttached"],
                inputOnChange: restoreLinkedWorksite,
              },
              {
                widget: "radio",
                choice:
                  "Employer's private household (includes live-in and domestic household worker)",
                value: "2",
                validate: ["required"],
                disabled: true,
                disableWhenField: {
                  when: "occupationType",
                  is: "professional_athlete",
                },
                conditionalClearSchema: conditionalClearSchema,
                clearSchema: ["isMultipleLocations", "isAppdendixBAttached"],
                inputOnChange: restoreLinkedWorksite,
              },
              {
                widget: "radio",
                choice:
                  "Employee's private residence (when work is performed directly out of the residence)",
                value: "3",
                validate: ["required"],
                disabled: true,
                disableWhenField: {
                  when: "occupationType",
                  is: "professional_athlete",
                },
                conditionalClearSchema: conditionalClearSchema,
                clearSchema: ["isMultipleLocations", "isAppdendixBAttached"],
                inputOnChange: restoreLinkedWorksite,
              },
              {
                widget: "radio",
                choice:
                  "No <u>one</u> specific worksite address or physical location",
                value: "4",
                validate: ["required"],
                disabled: true,
                disableWhenField: {
                  when: "occupationType",
                  is: "professional_athlete",
                },
                setSchema: [
                  ...setTheseFieldsToNA,
                  { set: "isMultipleLocations", to: "Yes" },
                  { set: "isAppdendixBAttached", to: 1 },
                ],
              },
            ],
          },
        },
        {
          code: "F.a.2",
          machine_name: "primaryWorksiteAddr1",
          widget: "text",
          label: "Worksite Address",
          required: true,
          maxLength: 255,
          validate: [
            {
              isRequired: "occupationType",
              message: "This field is required",
              needsFieldValues: true,
              extraData: {
                valueOf: [
                  "professional_occupation",
                  "non_professional",
                  "college_university_teacher",
                ],
              },
            },
            {
              minLength: 2,
              message: "Worksite address 1 must contain at least 2 characters.",
            },
          ],
          disabled: true,
          disableWhenField: (formValues) => {
            return (
              formValues.occupationType === "professional_athlete" ||
              formValues.primaryWorksiteType === "4"
            );
          },
          tooltip:
            "Enter the street address of the worksite location identified in question 1, where work will be performed.  The worksite address must be a physical location and cannot be a P.O. Box.",
        },
        {
          code: "F.a.3",
          machine_name: "primaryWorksiteAddr2",
          widget: "text",
          label: "Worksite Address (apartment/suite/floor and number) §",
          tooltip: `If additional space is required for the street address, use this line.  If submitting this form non-electronically and no additional space is required, enter "N/A."`,
          disabled: true,
          maxLength: 255,
          disableWhenField: (formValues) => {
            return (
              formValues.occupationType === "professional_athlete" ||
              formValues.primaryWorksiteType === "4"
            );
          },
        },
        {
          code: "F.a.4",
          machine_name: "primaryWorksiteCity",
          widget: "text",
          label: "City",
          required: true,
          maxLength: 35,
          validate: [
            {
              isRequired: "occupationType",
              message: "This field is required",
              needsFieldValues: true,
              extraData: {
                valueOf: [
                  "professional_occupation",
                  "non_professional",
                  "college_university_teacher",
                ],
              },
            },
            {
              minLength: 2,
              message: "City must contain at least 2 characters.",
            },
          ],
          disabled: true,
          disableWhenField: (formValues) => {
            return (
              formValues.occupationType === "professional_athlete" ||
              formValues.primaryWorksiteType === "4"
            );
          },
          tooltip: `Enter the city of the worksite location.`,
        },
        {
          code: "F.a.6",
          label: "State/District/Territory",
          machine_name: "primaryWorksiteState",
          widget: "select",
          selection: (formApi, counties) =>
            getStateSelectionOptions({
              formApi,
              counties,
              msaFieldName: "primaryWorksiteBlsArea",
              multiSiteField: "primaryWorksiteType",
              multiSiteValue: "4",
              linkingFieldName: "faWorksiteLinked",
              defaultStates: data.state,
            }),
          state: true,
          maxLength: 35,
          required: true,
          validate: [
            {
              isRequired: "occupationType",
              message: "This field is required",
              needsFieldValues: true,
              extraData: {
                valueOf: [
                  "professional_occupation",
                  "non_professional",
                  "college_university_teacher",
                ],
              },
            },
          ],
          tooltip: `Enter the state/district/territory of the worksite location.`,
          disabled: true,
          disableWhenField: (formValues) => {
            return (
              formValues.occupationType === "professional_athlete" ||
              formValues.primaryWorksiteType === "4"
            );
          },
        },
        {
          code: "F.a.5",
          machine_name: "primaryWorksiteCounty",
          widget: "countyselect_bls",
          label: "County",
          required: true,
          naOptionWhen: {
            when: "primaryWorksiteType",
            is: "4",
          },
          validate: [
            {
              isRequired: "occupationType",
              message: "This field is required",
              needsFieldValues: true,
              extraData: {
                valueOf: [
                  "professional_occupation",
                  "non_professional",
                  "college_university_teacher",
                ],
              },
            },
            {
              required_county: "primaryWorksiteState",
              message: "",
              needsFieldValues: true,
            },
          ],
          stateField: "primaryWorksiteState",
          disabled: true,
          disableWhenField: (formValues) => {
            return (
              formValues.occupationType === "professional_athlete" ||
              formValues.primaryWorksiteType === "4"
            );
          },
          tooltip: `Enter the county of the worksite location.`,
          updateBls: "primaryWorksiteBlsArea",
          updateBlsCode: "primaryWorksiteBlsAreaCode",
          restrictCountyOptions: (formApi) =>
            !!formApi.getState().values.faWorksiteLinked,
          countySource: (formApi, counties) => {
            const pwdCountySource = getCountySourceFromPwd({
              formStateValues: formApi.getState().values,
              linkingFieldName: "faWorksiteLinked",
              msaFieldName: "primaryWorksiteBlsArea",
              filterByMsa: true,
            });
            return pwdCountySource === null ? counties : pwdCountySource;
          },
        },
        {
          code: "F.a.7",
          machine_name: "primaryWorksitePostalCode",
          widget: "text",
          label: "Postal Code",
          required: true,
          validate: [
            {
              isRequired: "occupationType",
              message: "This field is required",
              needsFieldValues: true,
              extraData: {
                valueOf: [
                  "professional_occupation",
                  "non_professional",
                  "college_university_teacher",
                ],
              },
            },
          ],
          disabled: true,
          disableWhenField: (formValues) => {
            return (
              formValues.occupationType === "professional_athlete" ||
              formValues.primaryWorksiteType === "4"
            );
          },
          maxLength: 10,
          tooltip: `Enter the postal (zip) code of the worksite location.`,
        },
        {
          code: "F.a.8",
          machine_name: "primaryWorksiteBlsAreaCode",
          widget: "text",
          label: "MSA/OES Area Code",
          disabled: true,
          required: true,
          validate: [
            {
              isRequired: "occupationType",
              message: "This field is required",
              needsFieldValues: true,
              extraData: {
                valueOf: [
                  "professional_occupation",
                  "non_professional",
                  "college_university_teacher",
                ],
              },
            },
          ],
          tooltip:
            "Enter the Metropolitan Statistical Area (MSA) name/Occupational Employment Statistics (OES) area code covering the worksite location.",
        },
        {
          code: "F.a.8.a",
          machine_name: "primaryWorksiteBlsArea",
          widget: "text",
          label: "MSA Name/OES Area Title",
          disabled: true,
          required: true,
          validate: [
            {
              isRequired: "occupationType",
              message: "This field is required",
              needsFieldValues: true,
              extraData: {
                valueOf: [
                  "professional_occupation",
                  "non_professional",
                  "college_university_teacher",
                ],
              },
            },
          ],
          tooltip: `Enter the MSA/OES area title covering the worksite location.`,
        },
      ],
    },
  ],
};
